import { CareplanFilter, CareplanStatusFilter, RoDashboardTabs } from 'op-enums';
import { BaseConfigInterface } from './types';
import { BOOLEAN_OPTIONS, BOOLEAN_TBC_OPTIONS } from '../TreatmentInfoPage/Interface';

const au_config: BaseConfigInterface = {
  showPreferredName: true,
  SpecialPhysicsConsultVisible: false,
  ErrorPopup: {
    Generic:
      "We encountered an error. Please refresh the page. If the issue persists, please contact GenesisCare IT support: <a href='mailto:itsupport@genesiscare.com.au'>itsupport@genesiscare.com.au</a>",
    Practitioner:
      "We encountered an error. Please refresh the page. If the issue persists, please contact GenesisCare IT support: <a href='mailto:itsupport@genesiscare.com.au'>itsupport@genesiscare.com.au</a>",
    Patient: 'We encountered an error. Please refresh the page. If the issue persists, please contact your clinic.',
  },
  ReferringPhysicians: {
    oncologist: 'Responsible Physician',
    generalPractitioner: 'Primary Physician (GP)',
    referringSurgeon: 'Referring Physician 1',
    referringSurgeon2: 'Referring Physician 2',
    referringSurgeon3: 'Referring Physician 3',
  },
  DefaultValue: {
    cied: '',
    patientStatus: 'unknown',
    previousRadioTreatment: '',
    treatmentRORequired: '',
    tumourName: 'tumour ',
  },
  Clinex: {
    betaTesting: false,
  },
  Diagnosis: {
    defaultMStageTypeFilter: 'Clinical',
  },
  Intake: {
    disabled: false,
    isProstateDisabled: false,
    breastMandatoryFields: ['grade', 'er', 'pgr', 'her2'],
    lungMandatoryFields: ['weightLoss', 'smokingHistory', 'egfr', 'alk', 'pdl1'],
    headNeckMandatoryFields: ['P16', 'smokingHistory'],
    lymphomaMandatoryFields: ['stage', 'aOrBSymptoms', 'extranodal', 'bulkDisease'],
    breastFormVersion: 2,
    breastFormShowExercise: true,
  },
  PatientSummary: {
    isVisible: true,
    labsEnabled: true,
    RODocumentUpload: false,
    documentFilterField: 'description',
    hideInsuranceTab: false,
    summaryLabel: 'Patient Chart',
  },
  OutcomesPage: {
    isVisible: true,
    DateFormat: 'DD-MMM-YYYY',
    DateTimeFormat: 'DD MMM YYYY h:mma',
  },
  OTRPage: {
    isVisible: false,
    DateFormat: 'DD-MMM-YYYY',
    DateTimeFormat: 'DD MMM YYYY h:mma',
  },
  DisplayButton: {
    displaySimButton: true,
  },
  DisplayModal: {
    submission: false,
  },
  TreatmentInfoPage: {
    mandatoryFields: ['intent', 'treatingDepartment', 'readyForCareDate', 'cied', 'previousRadioTreatment'],
    ReadyForCareDate: true,
    AdditionalBookingInfo: true,
    MDT: false,
    AuthorizedICDTreatingDepartments: [],
    Hormones: true,
    Brachytherapy: true,
    ccip: false,
    TreatmentStartDate: true,
    ConsentStatus: false,
    PharmacyPrescription: false,
    PerformanceStatus: true,
  },
  SimulationPage: {
    SpacerGelWarning: false,
    imagingText: 'Add imaging request',
    imagingCountTitle: 'Imaging',
    disableExtentDefined: true,
    disablePositionSetup: true,
    immobilisationLabel: 'Immobilisation',
  },
  PrescriptionPage: {
    MicrodosimetryTld: false,
    WeeklyPhysicsCheck: false,
    BasicDoseCalculation: false,
  },
  SubmissionPage: {
    PlanAimSubmissionType: 'Plan Aim',
    PlanAimHidden: false,
  },
  VolumingPage: {
    UsePrioritizedOARs: false,
    TargetValueDoseSuffixEnabled: false,
  },
  RadiationDashboard: {
    dateTimeFormat: 'DD/MM/YYYY hh:mm A',
    active: RoDashboardTabs.APPOINTMENTS,
    careplanDashboard: {
      careplanStatusFilters: [
        { id: CareplanStatusFilter.DRAFT, name: CareplanStatusFilter.DRAFT, checked: true },
        { id: CareplanStatusFilter.SIMULATION, name: CareplanStatusFilter.SIMULATION, checked: true },
        { id: CareplanStatusFilter.PLAN_AIM, name: CareplanStatusFilter.PLAN_AIM, checked: true },
        { id: CareplanStatusFilter.PRESCRIPTION, name: CareplanStatusFilter.PRESCRIPTION, checked: true },
        { id: CareplanStatusFilter.CPOT, name: CareplanStatusFilter.CPOT, checked: true },
      ],
      careplanFilters: [{ id: CareplanFilter.UNSUBMITTED, name: CareplanFilter.UNSUBMITTED, checked: false }],
      isPatientSummaryLinkEnabled: true,
    },
    showFilters: false,
  },
  QuestionLabel: {
    CIED: 'Cardiac Implantable Electronic Device (CIED)',
    RoRequired: 'RO Required',
    GoldSeeds: 'Gold Seeds',
    phase: 'Phase',
  },
  QuestionOptions: {
    CIED: BOOLEAN_TBC_OPTIONS,
    previousRadioTreatment: BOOLEAN_OPTIONS,
  },
  QuestionValidationChildren: {
    CIED: ['ciedProtocolRequired', 'preTreatmentCiedCheckRequired'],
    Staging: {
      Primary: {
        RO: ['laterality', 'diagnosisDate'],
        MO: ['laterality'],
      },
      Metastasis: {
        RO: ['laterality', 'diagnosisDate'],
        MO: [],
      },
    },
  },
  ResetMutationVariableDefaults: {
    CIED: {
      ciedProtocolRequired: '',
      preTreatmentCiedCheckRequired: '',
      referralToGcCardiologist: '',
      otherCiedInstructions: '',
    },
    CiedProtocolRequired: {
      preTreatmentCiedCheckRequired: '',
      referralToGcCardiologist: '',
      otherCiedInstructions: '',
    },
  },
  GetMutationVariableDefaults: {
    CIED: {
      ciedProtocolRequired: 'yes',
      preTreatmentCiedCheckRequired: 'yes',
      referralToGcCardiologist: 'no',
      otherCiedInstructions: '',
    },
    CiedProtocolRequired: {
      preTreatmentCiedCheckRequired: 'yes',
      referralToGcCardiologist: 'no',
      otherCiedInstructions: '',
    },
  },
  DoseSiteSummary: {
    enabled: true,
    DateFormat: 'DD/MM/YYYY',
  },
  CardioReferralField: true,
  Tooltips: {
    hasPriority: false,
    hasClinicalPathologicalStaging: false,
  },
  UseDocumentTimezone: false,
  DefaultTimezone: 'Australia/Sydney',
  DateFormat: 'DD MM YYYY',
  ShortDateFormat: 'DD/MM/YYYY',
  DiagnosisDateFormat: 'DD-MMM-YYYY',
  CareplanVoidDescription: 'Documentation will be voided and a clinic note will be added into Mosaiq.',
  EmailMaxLength: 40,
  Outage: {
    link: '',
    showBanner: false,
    message: '',
    campaignId: '',
    startDate: '',
    endDate: '',
  },
  Notes: {
    DateTimeFormat: 'DD/MM/YYYY hh:mm A',
  },
  DefaultLocale: 'en-gb',
  SupportEmail: 'itsupport@genesiscare.com.au',
  ShowResetDemoDataMenuLink: false,
  Insights: {
    mockData: true,
    chartDateFormat: 'DD/MM/YYYY',
    Dashboards: {
      careplanAdherence: false,
      techniques: true,
      averageFractions: true,
      totalReferrals: false,
      totalDiagnosis: true,
      totalPlans: false,
      linacs: true,
      simToTreatment: true,
      averageDailyPlans: false,
      totalConsultations: false,
      totalSimulations: true,
      totalTreatments: true,
    },
  },
  PatientCard: {
    appointmentDateFormat: 'ddd, Do MMM YYYY',
    defaultDateFormat: 'DD-MMM-YYYY',
    defaultAlternateDateFormat: 'DD MMM YYYY',
    dateOfBirthFormat: 'Do MMM YYYY',
  },
  AISummary: {
    lastUpdatedDateTimeFormat: 'DD MMM YYYY, h:mm A',
  },
};

export default au_config;
